import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Users, UserX, UserCheck, Shield, ShieldOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AppHeader from "@/components/AppHeader";
import EmptyState from "@/components/EmptyState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const usersPageUrl = (query, skip) =>
  `${API}/admin/users?limit=${PAGE_SIZE}&skip=${skip}&q=${encodeURIComponent(query)}`;

const UserManagement = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(null);
  const [grantAdminDialogOpen, setGrantAdminDialogOpen] = useState(null);
  const [revokeAdminDialogOpen, setRevokeAdminDialogOpen] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (!response.data.is_admin) {
          toast.error('Access denied');
          navigate('/dashboard');
          return;
        }
        setCurrentUser(response.data);
        await fetchUsers(debouncedQuery);
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate('/');
        } else {
          toast.error('Failed to load users. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuthAndFetch();
  }, [navigate, debouncedQuery]);

  const fetchUsers = async (query) => {
    const [list, count] = await Promise.all([
      axios.get(usersPageUrl(query, 0)),
      axios.get(`${API}/admin/users/count?q=${encodeURIComponent(query)}`),
    ]);
    setUsers(list.data);
    setUserCount(count.data.count);
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(usersPageUrl(debouncedQuery, users.length));
      setUsers((prev) => [...prev, ...data]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Could not load more users. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshUsers = async () => {
    try {
      await fetchUsers(debouncedQuery);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  const handleBlockUser = async (userId, shouldBlock) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/block`, { is_blocked: shouldBlock });
      toast.success(shouldBlock ? 'User blocked successfully' : 'User unblocked successfully');
      await refreshUsers();
      setBlockDialogOpen(null);
    } catch (error) {
      console.error('Block error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleToggleAdmin = async (userId, shouldBeAdmin) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/admin`, { is_admin: shouldBeAdmin });
      toast.success(shouldBeAdmin ? 'Admin privileges granted' : 'Admin privileges revoked');
      await refreshUsers();
      setGrantAdminDialogOpen(null);
      setRevokeAdminDialogOpen(null);
    } catch (error) {
      console.error('Admin toggle error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AppHeader
        right={
          <Button size="sm" variant="outline" onClick={() => navigate('/admin')} data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Panel
          </Button>
        }
      />

      <div className="dashboard-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: 700 }} data-testid="user-management-title">
              Manage Users
            </h1>
            <p style={{ fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
              {userCount.toLocaleString()} users
            </p>
          </div>
          <div style={{ position: 'relative', maxWidth: '400px', flex: '1', minWidth: '250px' }}>
            <Search className="w-4 h-4" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
              data-testid="user-search-input"
            />
          </div>
        </div>

        {users.length > 0 && (
          <Card>
            <div style={{ overflowX: 'auto' }}>
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead style={{ textAlign: 'right' }}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div style={{ fontWeight: 600 }}>{user.name}</div>
                        <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>{user.email}</div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {user.is_admin && (
                            <Badge variant="secondary">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </Badge>
                          )}
                          {user.is_blocked && <Badge variant="destructive">Blocked</Badge>}
                          {!user.is_admin && !user.is_blocked && (
                            <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>Active</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap' }}>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                          {user.id === currentUser.id ? (
                            <Badge variant="outline">This is you</Badge>
                          ) : (
                            <>
                              {!user.is_blocked ? (
                                <Button variant="ghost" size="sm" title="Block user"
                                  style={{ color: 'hsl(var(--destructive))' }}
                                  onClick={() => setBlockDialogOpen(user)}
                                  data-testid={`block-user-${user.id}`}>
                                  <UserX className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" title="Unblock user"
                                  style={{ color: 'hsl(var(--primary))' }}
                                  onClick={() => handleBlockUser(user.id, false)}
                                  data-testid={`unblock-user-${user.id}`}>
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                              )}
                              {!user.is_admin ? (
                                <Button variant="ghost" size="sm" title="Grant admin"
                                  onClick={() => setGrantAdminDialogOpen(user)}
                                  data-testid={`grant-admin-${user.id}`}>
                                  <Shield className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" title="Revoke admin"
                                  onClick={() => setRevokeAdminDialogOpen(user)}
                                  data-testid={`revoke-admin-${user.id}`}>
                                  <ShieldOff className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}

        <Dialog open={blockDialogOpen !== null} onOpenChange={(open) => !open && setBlockDialogOpen(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Block User</DialogTitle>
              <DialogDescription>
                Are you sure you want to block {blockDialogOpen?.name}? They will no longer be able to access the app.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlockDialogOpen(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => handleBlockUser(blockDialogOpen.id, true)}>
                Block User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={grantAdminDialogOpen !== null} onOpenChange={(open) => !open && setGrantAdminDialogOpen(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Grant Admin Privileges</DialogTitle>
              <DialogDescription>
                Are you sure you want to grant admin privileges to {grantAdminDialogOpen?.name}? They will have full access to admin features.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGrantAdminDialogOpen(null)}>Cancel</Button>
              <Button onClick={() => handleToggleAdmin(grantAdminDialogOpen.id, true)}>
                Grant Admin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={revokeAdminDialogOpen !== null} onOpenChange={(open) => !open && setRevokeAdminDialogOpen(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke Admin Privileges</DialogTitle>
              <DialogDescription>
                Are you sure you want to revoke admin privileges from {revokeAdminDialogOpen?.name}?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevokeAdminDialogOpen(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => handleToggleAdmin(revokeAdminDialogOpen.id, false)}>
                Revoke Admin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {users.length === 0 && (
          <EmptyState
            icon={Users}
            title={searchQuery ? 'No users found' : 'No users yet'}
            hint={searchQuery ? 'Try a different name or email.' : undefined}
            testId="empty-users"
          />
        )}

        {users.length > 0 && users.length < userCount && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Button
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="load-more-users-button"
            >
              {loadingMore ? 'Loading…' : `Load more (${users.length} of ${userCount.toLocaleString()})`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
